import type {
  MentionCandidate,
  StudyGroupMention,
  User,
} from "@/lib/humhub-api/types";

/**
 * テキスト内の @ 記号数を返す
 */
export const countAtSigns = (text: string): number =>
  (text.match(/@/g) || []).length;

/**
 * メンション要素を作成する
 */
export const createMentionElement = (
  mentionText: string,
): HTMLAnchorElement => {
  const mentionElement = document.createElement("a");
  mentionElement.contentEditable = "false";
  mentionElement.className = "mention-link";
  mentionElement.style.cssText = `
    color: var(--rw-mention-link) !important;
    text-decoration: none !important;
    cursor: pointer !important;
    user-select: none !important;
  `;

  mentionElement.textContent = mentionText;

  return mentionElement;
};

/**
 * 要素内のメンション要素をマークダウンテキストに変換する
 */
export const convertMentionsInElement = (element: HTMLElement): void => {
  const mentionElements = element.querySelectorAll(
    ".mention-link[data-mention]",
  );
  mentionElements.forEach((mentionEl) => {
    const mentionText = mentionEl.getAttribute("data-mention");
    if (mentionText) {
      const textNode = document.createTextNode(mentionText);
      mentionEl.parentNode?.replaceChild(textNode, mentionEl);
    }
  });
};

/**
 * 検索クエリとテキストのマッチ度を計算（0-100）
 */
const calculateMatchScore = (query: string, text: string): number => {
  if (!query) return 100; // 空クエリは全てマッチ

  const normalizedQuery = query.toLowerCase();
  const normalizedText = text.toLowerCase();

  // 完全一致
  if (normalizedText === normalizedQuery) return 100;

  // 前方一致
  if (normalizedText.startsWith(normalizedQuery)) return 90;

  // 含有
  if (normalizedText.includes(normalizedQuery)) return 70;

  // 単語の前方一致（スペース区切り）
  const words = normalizedText.split(" ");
  for (const word of words) {
    if (word.startsWith(normalizedQuery)) return 60;
  }

  // 部分的な文字一致
  let matchCount = 0;
  for (const char of normalizedQuery) {
    if (normalizedText.includes(char)) matchCount++;
  }
  const partialScore = (matchCount / normalizedQuery.length) * 40;

  return Math.max(0, partialScore);
};

/**
 * User を MentionCandidate に変換
 */
export const userToMentionCandidate = (
  user: User,
  searchQuery = "",
): MentionCandidate => {
  const displayName = user.display_name;
  const username = user.account?.username || "";

  // 表示名とユーザー名の両方でマッチ度を計算し、高い方を採用
  const displayNameScore = calculateMatchScore(searchQuery, displayName);
  const usernameScore = calculateMatchScore(searchQuery, username);
  const matchScore = Math.max(displayNameScore, usernameScore);

  return {
    id: `user_${user.id}`,
    displayName,
    secondaryText: undefined,
    avatarUrl: user.image_url,
    type: "user",
    originalData: user,
    matchScore,
  };
};

/**
 * StudyGroup を MentionCandidate に変換
 */
export const studyGroupToMentionCandidate = (
  studyGroup: StudyGroupMention,
  searchQuery = "",
): MentionCandidate => {
  const matchScore = calculateMatchScore(searchQuery, studyGroup.name);

  return {
    id: `study_group_${studyGroup.guid}`,
    displayName: studyGroup.name,
    secondaryText: "Study Group",
    avatarUrl: studyGroup.imageUrl || "/default-study-group-icon.svg",
    type: "study_group",
    originalData: studyGroup,
    matchScore,
  };
};

/**
 * MentionCandidate をマッチ度でソート
 */
export const sortMentionCandidatesByMatch = (
  candidates: MentionCandidate[],
): MentionCandidate[] => {
  return candidates.sort((a, b) => {
    // マッチ度が高い順
    const scoreDiff = (b.matchScore || 0) - (a.matchScore || 0);
    if (scoreDiff !== 0) return scoreDiff;

    // マッチ度が同じ場合は、スタディグループを優先
    if (a.type === "study_group" && b.type === "user") return -1;
    if (a.type === "user" && b.type === "study_group") return 1;

    // 同じタイプの場合は名前順
    return a.displayName.localeCompare(b.displayName);
  });
};

/**
 * ユーザーからメンションテキストを生成する
 */
export const generateMentionText = (user: User): string => {
  const displayName = user.display_name || "Unknown User";
  const guid = user.guid || "";

  return `[${displayName}](mention:${guid})`;
};

/**
 * スタディグループからメンションテキストを生成する
 */
export const generateStudyGroupMentionText = (
  studyGroup: StudyGroupMention,
): string => {
  return `[${studyGroup.name}](mention:${studyGroup.guid})`;
};

/**
 * MentionCandidate からメンションテキストを生成する
 */
export const generateMentionTextFromCandidate = (
  candidate: MentionCandidate,
): string => {
  if (candidate.type === "user") {
    return generateMentionText(candidate.originalData as User);
  }
  return generateStudyGroupMentionText(
    candidate.originalData as StudyGroupMention,
  );
};

/**
 * メンション選択処理を実行する（拡張版）
 */
export const handleMentionSelection = (
  candidate: MentionCandidate,
  editorRef: React.RefObject<HTMLDivElement | null>,
): void => {
  if (!editorRef.current) {
    return;
  }

  const mentionText = generateMentionTextFromCandidate(candidate);
  const displayName = candidate.displayName;

  editorRef.current.focus();

  const selection = window.getSelection();

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const currentNode = range.startContainer;

    if (currentNode.nodeType === Node.TEXT_NODE) {
      const text = currentNode.textContent || "";
      const position = range.startOffset;
      const beforeCursor = text.substring(0, position);
      const lastAtIndex = beforeCursor.lastIndexOf("@");

      if (lastAtIndex !== -1) {
        const beforeText = text.substring(0, lastAtIndex);
        const afterText = text.substring(position);

        // Create mention element
        const mentionElement = createMentionElement(`@${displayName}`);
        mentionElement.setAttribute("data-mention", mentionText);

        // スタディグループの場合は異なるスタイルを適用
        if (candidate.type === "study_group") {
          mentionElement.style.cssText += `
            background-color: var(--rw-mention-bg) !important;
            padding: 1px 4px !important;
            border-radius: 3px !important;
            font-weight: 500 !important;
          `;
        }

        // Replace the text node
        const parentNode = currentNode.parentNode;
        if (parentNode) {
          // Create document fragment
          const fragment = document.createDocumentFragment();

          if (beforeText) {
            fragment.appendChild(document.createTextNode(beforeText));
          }

          fragment.appendChild(mentionElement);
          fragment.appendChild(document.createTextNode(" "));

          if (afterText) {
            fragment.appendChild(document.createTextNode(afterText));
          }

          // Replace the text node
          parentNode.replaceChild(fragment, currentNode);

          // Set cursor after mention
          const newRange = document.createRange();
          newRange.setStartAfter(mentionElement);
          newRange.collapse(true);

          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
    } else {
      // Handle non-text node case
      const mentionElement = createMentionElement(`@${displayName}`);
      mentionElement.setAttribute("data-mention", mentionText);

      // スタディグループの場合は異なるスタイルを適用
      if (candidate.type === "study_group") {
        mentionElement.style.cssText += `
          background-color: var(--rw-mention-bg) !important;
          padding: 1px 4px !important;
          border-radius: 3px !important;
          font-weight: 500 !important;
        `;
      }

      // Insert at cursor position
      range.deleteContents();
      range.insertNode(mentionElement);

      // Add space after mention
      const spaceNode = document.createTextNode(" ");
      range.setStartAfter(mentionElement);
      range.insertNode(spaceNode);

      // Set cursor after space
      range.setStartAfter(spaceNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
};

/**
 * MentionCandidate がスタディグループかどうかを判定
 */
export const isStudyGroupCandidate = (candidate: MentionCandidate): boolean => {
  return candidate.type === "study_group";
};

/**
 * 従来のUser型メンション選択（後方互換性のため）
 */
export const handleUserMentionSelection = (
  user: User,
  editorRef: React.RefObject<HTMLDivElement | null>,
): void => {
  const candidate = userToMentionCandidate(user);
  handleMentionSelection(candidate, editorRef);
};
